import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronUp,
  ChevronDown,
  CalendarPlus,
  Cake,
  Heart,
  Plane,
  Wallet,
  CalendarDays,
  Eye,
  EyeOff,
} from "lucide-react";
import {
  addDays,
  monday,
  daysBetween,
  lunarLabel,
  shortDate,
  todayIn,
  dateToCalendar,
  type Entity,
  type Holiday,
  type Occurrence,
  type User,
} from "@kkindex/shared";
import { api } from "./api";
import { Checkbox, Drop, Links, Modal } from "./components";
import type { Execute } from "./Editor";
import { InlineTitle } from "./InlineTitle";
import { useSelection } from "./interaction";
import { CalendarMenu } from "./CalendarMenu";
import { useCalendarDrag } from "./useCalendarDrag";
import {
  calendarDay,
  calendarLanes,
  calendarWeek,
  type CalendarBar as Bar,
} from "./calendarLayout";
import s from "./App.module.css";
export type CalendarData = {
  tasks: Entity[];
  events: Occurrence[];
  holidays: Holiday[];
};
const eventIcons = {
  cake: Cake,
  heart: Heart,
  plane: Plane,
  wallet: Wallet,
  calendar: CalendarDays,
};
const calendarSelection = (bar: Bar) =>
  `calendar:${bar.event?.id || bar.item.id}`;
export function Calendar({
  items,
  user,
  execute,
  edit,
  addEvent,
  mobile,
}: {
  items: Entity[];
  user: User;
  execute: Execute;
  edit: (e: Entity) => void;
  addEvent: () => void;
  mobile: boolean;
}) {
  const { selected, select } = useSelection();
  const [menu, setMenu] = useState<{
    id: string;
    date: string;
    x: number;
    y: number;
  } | null>(null);
  const resizeCleanup = useRef<(() => void) | null>(null);
  useEffect(() => () => resizeCleanup.current?.(), []);
  const today = todayIn(user.timezone),
    [base, setBase] = useState(addDays(monday(today), -70)),
    [visible, setVisible] = useState(addDays(monday(today), -14)),
    [hideDone, setHideDone] = useState(false),
    [detailDay, setDetailDay] = useState<string | null>(null),
    [resize, setResize] = useState<{
      id: string;
      start: string;
      end: string;
    } | null>(null);
  const scroll = useRef<HTMLDivElement>(null),
    rowHeight = useRef(116),
    initial = useRef(true),
    pending = useRef<number | null>(null),
    rangeRef = useRef(base);
  rangeRef.current = base;
  const from = mobile ? addDays(today, -1) : base,
    to = mobile ? addDays(today, 30) : addDays(base, 146);
  const query = useQuery({
    queryKey: ["calendar", from, to],
    queryFn: () => api<CalendarData>(`/calendar?from=${from}&to=${to}`),
  });
  const data = query.data || { tasks: [], events: [], holidays: [] };
  const sorting = useCalendarDrag(data.tasks, execute);
  const bars: Bar[] = [
    ...data.tasks
      .filter((t) => !hideDone || !t.done)
      .map((t) => ({ item: t, start: t.start!, end: t.end! })),
    ...data.events.flatMap((event) => {
      const item = items.find((e) => e.id === event.entityId);
      return item ? [{ item, event, start: event.start, end: event.end }] : [];
    }),
  ].map((b) =>
    resize?.id === b.item.id
      ? { ...b, start: resize.start, end: resize.end }
      : b,
  );
  const lanes = calendarLanes(bars);
  const menuItem =
    menu && items.find((item) => item.id === menu.id && !item.deletedAt);
  const menuTasks = menu
    ? calendarDay(
        data.tasks.map((item) => ({
          item,
          start: item.start!,
          end: item.end!,
        })),
        menu.date,
      ).map((b) => b.item)
    : [];
  const menuVisibleTasks = menuTasks.filter((item) => !hideDone || !item.done);
  const menuIndex = menuVisibleTasks.findIndex((item) => item.id === menu?.id);
  function reorderDay(direction: number) {
    if (!menu || menuIndex < 0 || !menuVisibleTasks[menuIndex + direction])
      return;
    const ordered = [...menuTasks];
    const source = ordered.findIndex((item) => item.id === menu.id);
    const target = ordered.findIndex(
      (item) => item.id === menuVisibleTasks[menuIndex + direction].id,
    );
    [ordered[source], ordered[target]] = [ordered[target], ordered[source]];
    void execute("/calendar/reorder", "POST", {
      date: menu.date,
      ids: ordered.map((item) => item.id),
      revisions: Object.fromEntries(
        ordered.map((item) => [item.id, item.revision]),
      ),
    }).catch(() => {});
  }
  useLayoutEffect(() => {
    if (!scroll.current || mobile) return;
    const el = scroll.current;
    const observer = new ResizeObserver(() => {
      const old = rowHeight.current,
        newHeight = Math.max(108, el.clientHeight / 5);
      rowHeight.current = newHeight;
      el.style.setProperty("--week-height", `${newHeight}px`);
      el.scrollTop = initial.current
        ? newHeight * 8
        : (el.scrollTop / old) * newHeight;
      initial.current = false;
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [mobile]);
  useLayoutEffect(() => {
    if (scroll.current && pending.current !== null) {
      scroll.current.scrollTop = pending.current;
      pending.current = null;
    }
  }, [base]);
  useEffect(() => {
    setBase(addDays(monday(today), -70));
    setVisible(addDays(monday(today), -14));
    initial.current = true;
    if (scroll.current) scroll.current.scrollTop = rowHeight.current * 8;
  }, [today]);
  function onScroll() {
    const el = scroll.current!;
    const index = Math.floor((el.scrollTop + 0.5) / rowHeight.current);
    setVisible(addDays(base, index * 7));
    if (pending.current !== null) return;
    if (index < 3) {
      pending.current = el.scrollTop + 7 * rowHeight.current;
      setBase(addDays(base, -49));
    } else if (index > 13) {
      pending.current = el.scrollTop - 7 * rowHeight.current;
      setBase(addDays(base, 49));
    }
  }
  function pointDate(x: number, y: number) {
    const el = scroll.current!,
      rect = el.getBoundingClientRect();
    const day = Math.max(
        0,
        Math.min(6, Math.floor((x - rect.left) / (rect.width / 7))),
      ),
      week = Math.max(
        0,
        Math.min(
          20,
          Math.floor((y - rect.top + el.scrollTop) / rowHeight.current),
        ),
      );
    return addDays(rangeRef.current, week * 7 + day);
  }
  function resizeStart(
    event: React.PointerEvent,
    item: Bar,
    edge: "start" | "end",
  ) {
    if (event.button !== 0 || selected !== calendarSelection(item)) return;
    event.preventDefault();
    event.stopPropagation();
    (event.currentTarget as HTMLElement).focus();
    const initial = { id: item.item.id, start: item.start, end: item.end };
    let draft = initial;
    const startPoint = { x: event.clientX, y: event.clientY };
    let activated = false;
    const move = (e: PointerEvent) => {
      if (
        !activated &&
        Math.hypot(e.clientX - startPoint.x, e.clientY - startPoint.y) < 8
      )
        return;
      activated = true;
      const rect = scroll.current!.getBoundingClientRect();
      if (e.clientY < rect.top + 35) scroll.current!.scrollTop -= 8;
      else if (e.clientY > rect.bottom - 35) scroll.current!.scrollTop += 8;
      const date = pointDate(e.clientX, e.clientY);
      draft = {
        ...draft,
        [edge]:
          edge === "start"
            ? date > draft.end
              ? draft.end
              : date
            : date < draft.start
              ? draft.start
              : date,
      };
      setResize(draft);
    };
    const finish = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", cancel);
      window.removeEventListener("keydown", key);
      resizeCleanup.current = null;
      setResize(null);
      if (draft.start === initial.start && draft.end === initial.end) return;
      if (item.item.kind === "event") {
        if (
          item.item.repeat === "yearly" &&
          !confirm("调整日期将修改整个年度事项系列，继续？")
        )
          return;
        void execute(`/items/${item.item.id}`, "PATCH", {
          ...dateToCalendar(draft.start, item.item.calendar),
          ...(item.item.repeat === "yearly" ? { year: item.item.year } : {}),
          duration: daysBetween(draft.start, draft.end) + 1,
          revision: item.item.revision,
        }).catch(() => {});
      } else
        void execute(`/items/${item.item.id}`, "PATCH", {
          start: draft.start,
          end: draft.end,
          revision: item.item.revision,
        }).catch(() => {});
    };
    const cancel = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", cancel);
      window.removeEventListener("keydown", key);
      resizeCleanup.current = null;
      setResize(null);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") cancel();
    };
    resizeCleanup.current = cancel;
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", finish, { once: true });
    window.addEventListener("pointercancel", cancel, { once: true });
    window.addEventListener("keydown", key);
  }
  function bar(
    b: Bar,
    segStart: string,
    segEnd: string,
    style?: CSSProperties,
  ) {
    const parent = items.find((p) => p.id === b.item.parentId),
      color =
        b.item.kind === "task" ? parent?.color || "#5c82d9" : b.item.color,
      Icon = eventIcons[b.item.icon as keyof typeof eventIcons] || CalendarDays;
    const selectionKey = calendarSelection(b);
    const active = selected === selectionKey;
    const dragging = sorting.drag?.id === b.item.id;
    const target = sorting.drag?.target;
    const dropping = target?.id === b.item.id && target.segment === segStart;
    return (
      <div
        role="group"
        aria-label={b.item.title}
        title={b.item.title}
        tabIndex={0}
        data-item-id={b.item.id}
        data-selection-key={selectionKey}
        data-selected={active}
        data-drag-state={dragging ? "dragging" : "idle"}
        data-calendar-task={!b.event}
        data-calendar-start={segStart}
        data-calendar-end={segEnd}
        onPointerDown={(event) =>
          sorting.start(
            event,
            b,
            mobile
              ? segStart
              : detailDay || pointDate(event.clientX, event.clientY),
            !mobile && active,
          )
        }
        onClickCapture={(event) => sorting.captureClick(event, b.item.id)}
        onClick={(event) => {
          if (
            !(event.target as Element).closest(
              "button,a,input,[role=separator]",
            )
          )
            select(selectionKey);
        }}
        onDoubleClick={(event) => {
          if (
            !(event.target as Element).closest(
              "button,a,input,[role=separator]",
            )
          )
            edit(b.item);
        }}
        onContextMenu={(event) => {
          event.preventDefault();
          event.stopPropagation();
          select(selectionKey);
          const date =
            mobile || detailDay
              ? segStart
              : pointDate(event.clientX, event.clientY);
          setMenu({
            id: b.item.id,
            date: date < segStart ? segStart : date > segEnd ? segEnd : date,
            x: event.clientX,
            y: event.clientY,
          });
        }}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return;
          if (event.key === "Enter") edit(b.item);
          if (event.key === " ") {
            event.preventDefault();
            select(selectionKey);
          }
          if (
            event.key === "ContextMenu" ||
            (event.shiftKey && event.key === "F10")
          ) {
            event.preventDefault();
            const rect = event.currentTarget.getBoundingClientRect();
            select(selectionKey);
            setMenu({
              id: b.item.id,
              date: segStart,
              x: rect.left,
              y: rect.bottom,
            });
          }
        }}
        key={`${b.item.id}-${segStart}`}
        className={`${s.bar} ${active ? s.selectedItem : ""} ${!mobile && active && !b.event ? s.calendarSortable : ""} ${dragging ? s.calendarDragging : ""} ${dropping ? (target.after ? s.calendarDropAfter : s.calendarDropBefore) : ""} ${b.event ? s.eventBar : ""} ${b.item.done && !b.event ? s.done : ""} ${!b.event && !b.item.done && b.end < today ? s.overdue : ""} ${b.start < segStart ? s.continuation : ""} ${b.end > segEnd ? s.continues : ""}`}
        style={
          {
            ...style,
            "--item-color": color,
            ...(dragging
              ? { transform: `translateY(${sorting.drag!.dy}px)` }
              : {}),
          } as CSSProperties
        }
      >
        {!mobile && !detailDay && active && b.start === segStart && (
          <span
            role="separator"
            tabIndex={-1}
            aria-label={`调整开始日期：${b.item.title}`}
            className={`${s.resizeHandle} ${s.resizeStart}`}
            onPointerDown={(e) => resizeStart(e, b, "start")}
          />
        )}
        {b.event ? (
          <Icon size={12} />
        ) : (
          <Checkbox
            done={b.item.done}
            label={b.item.title}
            onClick={() =>
              void execute(`/items/${b.item.id}`, "PATCH", {
                done: !b.item.done,
                revision: b.item.revision,
              }).catch(() => {})
            }
          />
        )}
        <InlineTitle
          item={b.item}
          selectionKey={selectionKey}
          execute={execute}
          edit={edit}
          className={s.barTitle}
          titleTooltip
        />
        <Links links={b.item.links} title={b.item.title} />
        {!mobile && !detailDay && active && b.end === segEnd && (
          <span
            role="separator"
            tabIndex={-1}
            aria-label={`调整结束日期：${b.item.title}`}
            className={`${s.resizeHandle} ${s.resizeEnd}`}
            onPointerDown={(e) => resizeStart(e, b, "end")}
          />
        )}
      </div>
    );
  }
  const rangeEnd = addDays(visible, 34),
    center = addDays(visible, 14);
  return (
    <section className={s.calendar} aria-label="日历">
      <header className={s.panelHead}>
        <div>
          <h1>
            {Number((mobile ? today : center).slice(5, 7))}月{" "}
            <small>{(mobile ? today : center).slice(0, 4)}</small>
          </h1>
        </div>
        <div className={s.inline}>
          <button
            className={s.outline}
            onClick={() => {
              setBase(addDays(monday(today), -70));
              setVisible(addDays(monday(today), -14));
              if (scroll.current)
                scroll.current.scrollTop = rowHeight.current * 8;
            }}
          >
            今天
          </button>
          <button
            aria-label="上一周"
            onClick={() =>
              scroll.current?.scrollBy({
                top: -rowHeight.current,
                behavior: "smooth",
              })
            }
          >
            <ChevronUp size={17} />
          </button>
          <button
            aria-label="下一周"
            onClick={() =>
              scroll.current?.scrollBy({
                top: rowHeight.current,
                behavior: "smooth",
              })
            }
          >
            <ChevronDown size={17} />
          </button>
          <button
            aria-label={hideDone ? "显示已完成任务" : "隐藏已完成任务"}
            onClick={() => setHideDone(!hideDone)}
          >
            {hideDone ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
          <button aria-label="添加日历事项" onClick={addEvent}>
            <CalendarPlus size={18} />
          </button>
        </div>
      </header>
      {mobile ? (
        <div className={s.agenda}>
          {Array.from({ length: 32 }, (_, i) => addDays(from, i))
            .filter((date) =>
              bars.some((b) => b.start <= date && b.end >= date),
            )
            .map((date) => (
              <section key={date} data-calendar-sort-scope>
                <h3>
                  {date === today ? "今天 · " : ""}
                  {shortDate(date)} <small>{lunarLabel(date).label}</small>
                </h3>
                {calendarDay(bars, date, lanes).map((b) => bar(b, date, date))}
              </section>
            ))}
          {!bars.length && <p className={s.empty}>近期还没有安排</p>}
        </div>
      ) : (
        <>
          <div className={s.weekdays}>
            {["周一", "周二", "周三", "周四", "周五", "周六", "周日"].map(
              (d) => (
                <span key={d}>{d}</span>
              ),
            )}
          </div>
          <div
            ref={scroll}
            className={s.weeks}
            onScroll={onScroll}
            data-testid="calendar-scroll"
          >
            {Array.from({ length: 21 }, (_, i) => {
              const week = addDays(base, i * 7);
              const layout = calendarWeek(bars, week, lanes);
              const visibleBars = layout.segments.map(
                ({ bar: b, first, last, lane }) =>
                  bar(b, addDays(week, first), addDays(week, last), {
                    gridColumn: `${first + 1} / ${last + 2}`,
                    gridRow: lane + 1,
                  }),
              );
              return (
                <div className={s.week} key={week} data-calendar-sort-scope>
                  <div className={s.dateGrid}>
                    {Array.from({ length: 7 }, (_, n) => {
                      const date = addDays(week, n),
                        lunar = lunarLabel(date),
                        holiday = data.holidays.find((h) => h.date === date);
                      return (
                        <Drop
                          id={`date:${date}`}
                          key={date}
                          data={{ date, surface: "date" }}
                          className={`${s.day} ${date === today ? s.today : ""}`}
                        >
                          <div className={s.dateLabel}>
                            <span
                              className={date === today ? s.todayNumber : ""}
                            >
                              {date.endsWith("-01")
                                ? `${Number(date.slice(5, 7))}月1`
                                : Number(date.slice(8, 10))}
                            </span>
                            {holiday && (
                              <em
                                className={
                                  holiday.type === "public_holiday"
                                    ? s.rest
                                    : s.work
                                }
                                title={holiday.name}
                              >
                                {holiday.type === "public_holiday"
                                  ? "休"
                                  : "班"}
                              </em>
                            )}
                            <small className={lunar.festival ? s.festival : ""}>
                              {lunar.label}
                            </small>
                          </div>
                        </Drop>
                      );
                    })}
                  </div>
                  <div className={s.lanes}>{visibleBars}</div>
                  <div className={s.overflowGrid}>
                    {layout.overflow.map(({ date, tasks, events }) => {
                      return (
                        <div key={date} data-calendar-overflow={date}>
                          {(tasks > 0 || events > 0) && (
                            <button onClick={() => setDetailDay(date)}>
                              {tasks > 0 ? `还有 ${tasks} 项` : ""}
                              {tasks > 0 && events > 0 ? " · " : ""}
                              {events > 0 ? `${events} 个事项` : ""}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <footer className={s.calendarFoot}>
            <span>
              {shortDate(visible)} — {shortDate(rangeEnd)}
            </span>
          </footer>
        </>
      )}
      {query.isError && <p className={s.error}>日历加载失败，请稍后重试</p>}
      {detailDay && (
        <Modal
          title={`${shortDate(detailDay)} 的安排`}
          onClose={() => setDetailDay(null)}
        >
          <div className={s.dayDetail} data-calendar-sort-scope>
            {calendarDay(bars, detailDay, lanes).map((b) =>
              bar(b, detailDay, detailDay),
            )}
          </div>
        </Modal>
      )}
      {menu && menuItem && (
        <CalendarMenu
          x={menu.x}
          y={menu.y}
          title={`${shortDate(menu.date)} · ${menuItem.title}`}
          onClose={() => setMenu(null)}
          actions={[
            ...(menuItem.kind === "task"
              ? [
                  {
                    label: "上移",
                    disabled: menuIndex <= 0,
                    run: () => reorderDay(-1),
                  },
                  {
                    label: "下移",
                    disabled:
                      menuIndex < 0 || menuIndex >= menuVisibleTasks.length - 1,
                    run: () => reorderDay(1),
                  },
                  {
                    label: "取消排期",
                    run: () => {
                      void execute(`/items/${menuItem.id}`, "PATCH", {
                        start: null,
                        end: null,
                        revision: menuItem.revision,
                      }).catch(() => {});
                    },
                  },
                ]
              : []),
            { label: "打开详情", run: () => edit(menuItem) },
            {
              label:
                menuItem.kind === "task"
                  ? "删除任务"
                  : menuItem.repeat === "yearly"
                    ? "删除整个年度系列"
                    : "删除事项",
              danger: true,
              run: () => {
                if (
                  menuItem.kind === "event" &&
                  menuItem.repeat === "yearly" &&
                  !confirm("删除将移除整个年度事项系列，继续？")
                )
                  return;
                void execute(
                  `/items/${menuItem.id}?revision=${menuItem.revision}`,
                  "DELETE",
                ).catch(() => {});
              },
            },
          ]}
        />
      )}
    </section>
  );
}
